import React, { useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { isValidString } from "@lifeat/shared/util/StringUtil";
import {
    APPLE_MUSIC_SRC_DOMAIN,
    SPOTIFY_SRC_DOMAIN,
    YOUTUBE_SRC_DOMAIN,
} from "@lifeat/shared/widget/MusicPlayerConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import RefreshIcon from "@icons/RefreshIcon.svg";
import { LocalStorageKeys, fetchFromLocalStorage, saveToLocalStorage } from "@service/LocalStorageService";
import { FontSize } from "@themes/Fonts";
import { getQueryParams } from "@util/RouteUtil";
import { UseMediaWidgetEvent } from "../../../AmplitudeWidgetEvents";
import Widget, { WidgetProps } from "../Widget";
import MediaPlayer from "./MediaPlayer";

const EmbedPlayerInputs = styled.div`
    display: flex;
    flex-direction: row;

    justify-content: space-between;
    align-items: center;

    width: 90%;
`;

const MediaContentLinkInput = styled.input`
    width: 90%;
    margin: 8px;
    padding: 4px;
    border-radius: 8px;
    font-size: 12px;

    background-color: transparent;
    color: ${(props) => props.theme.textColorPrimary};
    border: 1px ${(props) => props.theme.borderPrimary} solid;
`;

const SaveContentLinkButton = styled.span`
    color: ${(props) => props.theme.textColorSecondary};
`;

const ResetContentButton = styled.span`
    color: ${(props) => props.theme.textColorSecondary};
`;

const ResetIcon = styled(RefreshIcon)`
    stroke: ${(props) => props.theme.iconColor};
`;

const ActionButton = styled(LifeAtButton)`
    font-size: ${FontSize.normal};
    padding: unset;
    * {
        color: ${(props) => props.theme.textColorSecondary};
    }
`;

const LIFEAT_LOFI_SPOTIFY_PLAYLIST_URL = "https://open.spotify.com/embed/playlist/4aFB9we2DxFCIfJN4aClnE";
const LIFEAT_LOFI_YOUTUBE_PLAYLIST_URL =
    "https://www.youtube.com/embed/?listType=playlist&list=PLCRpZL7uefewJXuPZWd5Sljkaagh6s3-D";
// Test Data
// const LIFEAT_JAZZ_SPOTIFY_PLAYLIST_URL = "https://open.spotify.com/embed/playlist/3w5OafkXu3ACGIUZ1FaFeZ";
// const APPLE_MUSIC_PLAYLIST_URL = "https://embed.music.apple.com/us/playlist/todays-hits/pl.f4d106fed2bd41149aaacabb233eb5eb";
// const TOP_HITS_APPLE_MUSIC_PLAYLIST_URL = "https://music.apple.com/us/playlist/todays-hits/pl.f4d106fed2bd41149aaacabb233eb5eb";
// const YOUTUBE_VIDEO_BROWSER_ADDRESS_BAR_URL = "https://www.youtube.com/watch?v=zI383uEwA6Q"
// const YOUTUBE_VIDEO_SHARE_URL = "https://youtu.be/zI383uEwA6Q";
// const YOUTUBE_VIDEO_EMBED_URL = "https://www.youtube.com/embed/zI383uEwA6Q";
// const YOUTUBE_PLAYLIST_SHARE_URL = "https://youtube.com/playlist?list=PLQ-uHSnFig5PjfCy7mE77XMGhgky9HV3o";
// const YOUTUBE_PLAYLIST_BROWSER_ADDRESS_BAR_URL = YOUTUBE_PLAYLIST_SHARE_URL;
// const YOUTUBE_PLAYLIST_EMBED_URL = "https://www.youtube.com/embed/videoseries?list=PLQ-uHSnFig5PjfCy7mE77XMGhgky9HV3o";

const APPLE_MUSIC_EMBED_BASE = "https://embed.music.apple.com";
const SPOTIFY_EMBED_BASE = "https://open.spotify.com/embed/";
const YOUTUBE_EMBED_BASE = "https://www.youtube.com/embed/";
const ERROR_GIF_SRC = "https://i.giphy.com/media/CoND5j6Bn1QZUgm1xX/giphy.webp";

const WIDGET_HEADER_TITLE = "Media";
const URL_INPUT_PLACEHOLDER_NOT_DESKTOP = "Enter YouTube, Spotify, or Apple Music URL here";
const URL_INPUT_PLACEHOLDER_DESKTOP = "Enter YouTube URL here";
const SAVE_URL_BUTTON_LABEL = "Save";

interface MediaWidgetProps extends WidgetProps {
    readonly volume: number;
}
export default function MediaWidget(props: MediaWidgetProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const inputUrlRef = useRef<HTMLInputElement | null>(null);

    const defaultPlaylist = window.todesktop ? LIFEAT_LOFI_YOUTUBE_PLAYLIST_URL : LIFEAT_LOFI_SPOTIFY_PLAYLIST_URL;

    // Init the state to a saved spotify playlist link if the user has added one before.
    const [contentUrl, setContentUrl] = useState(
        fetchFromLocalStorage(LocalStorageKeys.SavedMusicPlaylist) || defaultPlaylist,
    );

    const headerContent = (
        <span
            data-for={"media-widget-title"}
            data-tip={
                window.todesktop
                    ? "Use LifeAt in your browser to listen to full songs on Spotify or Apple Music"
                    : "Sign into Spotify or Apple Music in your browser to listen to the full songs"
            }
        >
            {WIDGET_HEADER_TITLE}
            <ReactTooltip id={"media-widget-title"} place={"top"} />
        </span>
    );

    const updateSavedContentUrl = (newUrl: string | undefined) => {
        if (newUrl != null && newUrl.length > 0) {
            const builtUrl = parseMediaContentUrl(newUrl);

            if (canMediaUrlBeSaved(builtUrl)) {
                saveToLocalStorage(LocalStorageKeys.SavedMusicPlaylist, builtUrl);
            }

            if (builtUrl !== contentUrl) {
                setContentUrl(builtUrl);
            }
            if (inputUrlRef.current != null) {
                inputUrlRef.current.value = ""; // Reset so input is empty and placeholder text shows
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();

            const inputUrl = inputUrlRef.current?.value;
            if (isValidString(inputUrl)) {
                updateSavedContentUrl(inputUrl);

                // Content link was valid, so log an event
                GA4Service.logWidgetEvent(GA4Event.USE_CUSTOM_SPOTIFY_CONTENT);
                amplitudeService.logEvent(new UseMediaWidgetEvent());
            } else {
                updateSavedContentUrl(LIFEAT_LOFI_SPOTIFY_PLAYLIST_URL);
            }
        }
    };

    // Player content input field
    const footerContent = (
        <EmbedPlayerInputs>
            <MediaContentLinkInput
                name={`media-widget-content-url-input`}
                ref={inputUrlRef}
                autoComplete={"off"}
                placeholder={window.todesktop ? URL_INPUT_PLACEHOLDER_DESKTOP : URL_INPUT_PLACEHOLDER_NOT_DESKTOP}
                onKeyDown={(e) => handleKeyPress(e)}
                data-for={"media-widget-input-field"}
                data-tip={
                    window.todesktop
                        ? 'Paste a link to YouTube video/playlist, then press "enter"'
                        : 'Paste a link to YouTube video/playlist, or a Spotify or Apple Music playlist, album, or song, then press "enter"'
                }
            />
            <ReactTooltip id={"media-widget-input-field"} place={"bottom"} delayShow={100} />

            <SaveContentLinkButton>
                <ActionButton
                    isLink={false}
                    text={SAVE_URL_BUTTON_LABEL}
                    handleClick={() => updateSavedContentUrl(inputUrlRef.current?.value)}
                />
            </SaveContentLinkButton>

            <ResetContentButton>
                <ActionButton
                    isLink={false}
                    icon={<ResetIcon />}
                    handleClick={() => updateSavedContentUrl(LIFEAT_LOFI_SPOTIFY_PLAYLIST_URL)}
                />
            </ResetContentButton>
        </EmbedPlayerInputs>
    );

    return (
        <Widget
            headerContent={headerContent}
            footerContent={footerContent}
            overrideWidth={400}
            overrideHeight={240}
            {...props}
            lightMode={lightMode}
        >
            <MediaPlayer contentUrl={contentUrl} />
        </Widget>
    );
}

/**
 * Parse the user's inputted spotify URL to verify it is actually from a media
 * source that we support.
 * TODO: before adding anymore to this function, it should be split into helper functions and
 *      updated to use switch/case instead of if/else.
 * @param {string} urlInputStr The URL string the user inputted.
 * @returns {string} A built and valid media embed URL, or URL for error gif (if
 * the original user input is invalid).
 */
const parseMediaContentUrl = (urlInputStr: string): string => {
    if (urlInputStr.includes(APPLE_MUSIC_SRC_DOMAIN)) {
        // Example embed URL (from Apple Music): "https://embed.music.apple.com/us/playlist/todays-hits/pl.f4d106fed2bd41149aaacabb233eb5eb";
        // All we need to do is prepend the "embed" to the source domain
        if (urlInputStr.includes(APPLE_MUSIC_EMBED_BASE)) {
            // User pasted the embed link, so just return
            return urlInputStr;
        } else {
            // Splice the "embed" subdomain into the URL
            // Example pasted URL (e.g. "https://music.apple.com/us/playlist/todays-hits/pl.f4d106fed2bd41149aaacabb233eb5eb")
            const splitUrl = urlInputStr.split(APPLE_MUSIC_SRC_DOMAIN);
            const newUrl = splitUrl[0] + "embed." + APPLE_MUSIC_SRC_DOMAIN + splitUrl[1];
            return newUrl;
        }
    } else if (urlInputStr.includes(SPOTIFY_SRC_DOMAIN)) {
        // Split string on URL parameters (e.g. https://open.spotify.com/playlist/3w5OafkXu3ACGIUZ1FaFeZ?si=cf86b0992a0c4c8b&nd=1)
        // and remove the params.
        const urlWithoutQueryParams = urlInputStr.split("?")[0];
        if (urlWithoutQueryParams) {
            // Split the url on slashes (e.g. https://open.spotify.com/playlist/3w5OafkXu3ACGIUZ1FaFeZ)
            // so we can get the identifier of the content.
            const splitSlashesArr = urlWithoutQueryParams.split("/");

            // Get the part of the URL that contains the media type (e.g. playlist, album, etc)
            // so we have --> "playlist/3w5OafkXu3ACGIUZ1FaFeZ"
            const contentUrlStr = splitSlashesArr.slice(-2).join("/");

            // Concat the embed base URL with the content type and ID portion of the URL.
            return SPOTIFY_EMBED_BASE + contentUrlStr;
        } else {
            return ERROR_GIF_SRC;
        }
    } else if (urlInputStr.includes(YOUTUBE_SRC_DOMAIN) || urlInputStr.includes("https://youtu.be/")) {
        // See YouTube embed documentation here: https://developers.google.com/youtube/player_parameters#Selecting_Content_to_Play
        let vidId = "";
        if (urlInputStr.includes("/embed")) {
            // e.g. Embed URL: https://www.youtube.com/embed/VIDEO_ID
            const splitStr = urlInputStr.split("/embed");
            vidId = splitStr[1] ?? "";
        } else if (urlInputStr.includes("watch")) {
            // e.g. Browser address bar URL: https://www.youtube.com/watch?v=zI383uEwA6Q
            const splitStr = urlInputStr.split("?")[1];
            if (splitStr?.includes("v=")) {
                vidId = getQueryParams(splitStr).get("v") ?? "";
            } else {
                return ERROR_GIF_SRC;
            }
        } else if (urlInputStr.includes("playlist")) {
            // E.g. https://youtube.com/playlist?list=PLQ-uHSnFig5PjfCy7mE77XMGhgky9HV3o
            const splitStr = urlInputStr.split("?")[1];
            if (splitStr?.includes("list=")) {
                const listId = getQueryParams(splitStr).get("list");
                vidId = `?listType=playlist&list=${listId}`;
            }
        } else if (urlInputStr.includes("https://youtu.be/")) {
            // E.g. From share link: https://youtu.be/zI383uEwA6Q
            const splitStr = urlInputStr.split("youtu.be/");
            vidId = splitStr[1] ?? "";
        }

        if (vidId.length > 0) {
            return "https://www.youtube.com/embed/" + vidId;
        }
        return ERROR_GIF_SRC;
    } else {
        return ERROR_GIF_SRC;
    }
};

function canMediaUrlBeSaved(mediaUrl: string) {
    return (
        mediaUrl.includes(APPLE_MUSIC_EMBED_BASE) ||
        mediaUrl.includes(SPOTIFY_EMBED_BASE) ||
        mediaUrl.includes(YOUTUBE_EMBED_BASE)
    );
}
